function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-4"
      style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
    >
      {children}
    </h2>
  );
}

function SectionText({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-[#6B5E53] text-base leading-[1.9] mb-0"
      style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
    >
      {children}
    </p>
  );
}

function EditorialImage({ src, caption }: { src: string; caption: string }) {
  return (
    <div className="my-14 md:my-20 -mx-4 md:-mx-8">
      <div className="w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-2xl bg-[#EDE6DA]">
        <img
          src={src}
          alt={caption}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <p
        className="text-center text-[#B5A898] text-[11px] tracking-[0.15em] mt-4 uppercase"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {caption}
      </p>
    </div>
  );
}

export default function RCArticleContentFirstHalf() {
  return (
    <div className="max-w-2xl mx-auto px-4 md:px-0">
      {/* Intro paragraph */}
      <div className="pt-16 pb-12 border-b border-[#EDE6DA]">
        <p
          className="text-[#4A3F38] text-lg md:text-xl leading-[1.85]"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontStyle: "italic" }}
        >
          A cozy reading corner can become one of the most personal spaces in a home — a quiet retreat that belongs entirely to you. Even a modest armchair placed thoughtfully beside a lamp, a small table, and a few beloved books is enough. The magic is not in the size but in the intention. Warm neutrals, soft fabrics, natural wood, and gentle light do the rest.
        </p>
      </div>

      {/* Image 1 */}
      <EditorialImage
        src="https://public.readdy.ai/ai/img_res/88bce1aea7a315b173c3f7a27a9a81de.jpg"
        caption="Soft Neutral Reading Corner"
      />

      {/* Section 1 */}
      <div className="mb-14">
        <SectionHeading>Why cozy corners feel so inviting</SectionHeading>
        <SectionText>
          Cozy corners work because they do something most rooms cannot — they create a sense of defined, intimate scale within a larger space. When a room gives you one clear spot to slow down, the mind follows. The reduced visual noise, the enclosure of a corner, and the warmth of a single lamp all combine to create a psychological safety that is almost immediate. It is the opposite of open-plan living, and many people find it is the spot in the house they return to most.
        </SectionText>
      </div>

      {/* Section 2 */}
      <div className="mb-14">
        <SectionHeading>Start with a soft and comfortable seat</SectionHeading>
        <SectionText>
          The armchair is the heart of the reading corner, and it deserves careful thought. Look for a chair with a generous, slightly rounded shape — something that invites you to sink in rather than sit up. Upholstered in beige, cream, oatmeal, or warm taupe linen or bouclé, it should feel tactile from across the room. Avoid anything too firm, too angular, or too contemporary for this kind of space. The chair should feel like an old friend — broken in, welcoming, and completely unchallenging.
        </SectionText>
      </div>

      {/* Image 2 */}
      <EditorialImage
        src="https://readdy.ai/api/search-image?query=warm%20lamp%20light%20linen%20textures%20reading%20corner%20floor%20lamp%20soft%20glow%20cream%20armchair%20warm%20neutral%20interior%20linen%20cushion%20knit%20throw%20evening%20atmosphere%20calm%20cozy%20editorial%20photography%20beautiful%20serene&width=1400&height=600&seq=rc_img2&orientation=landscape"
        caption="Warm Lamp Light and Linen Textures"
      />

      {/* Section 3 */}
      <div className="mb-14">
        <SectionHeading>Use warm light to shape the mood</SectionHeading>
        <SectionText>
          Lighting is the single most powerful tool in a reading corner. A floor lamp positioned just over the shoulder of the chair, or a table lamp placed at the right height on a side table, can transform the entire feeling of the space. Choose bulbs with a warm colour temperature — somewhere between 2700K and 3000K — for a glow that feels amber and alive rather than cool and clinical. The pool of light from a single lamp defines the corner, softens everything within it, and makes the rest of the room feel gently distant.
        </SectionText>
      </div>

      {/* Section 4 */}
      <div className="mb-14">
        <SectionHeading>Add texture for warmth and depth</SectionHeading>
        <SectionText>
          In a neutral reading corner, texture carries the visual interest that colour does not. A linen cushion behind your back, a knitted throw draped over one arm of the chair, a boucle or brushed fabric on the seat, a woven rug beneath your feet, a matte ceramic vase on the side table — each of these adds a layer of tactile richness that makes the corner feel warm and considered. The more textures you include, the more dimensional and alive the space becomes, even when the palette stays deliberately quiet.
        </SectionText>
      </div>

      {/* Image 3 */}
      <EditorialImage
        src="https://readdy.ai/api/search-image?query=cozy%20armchair%20natural%20wood%20details%20oak%20side%20table%20ceramic%20mug%20linen%20cushion%20neutral%20reading%20corner%20warm%20morning%20light%20beige%20calm%20interior%20editorial%20photography%20beautiful%20atmosphere%20small%20space&width=1400&height=600&seq=rc_img3&orientation=landscape"
        caption="Cozy Chair and Natural Wood Details"
      />

      {/* Section 5 */}
      <div className="mb-14">
        <SectionHeading>Keep the layout simple and uncluttered</SectionHeading>
        <SectionText>
          The discipline of a great reading corner is in what you leave out. One chair, one lamp, one small side table, a few books — and then breathing space around all of it. Do not crowd the corner with extra furniture, multiple lamps, or objects that pull attention. The empty floor around the chair is part of the design. It signals that this is a place to rest, not to manage. A simple footstool or a low woven pouf beside the chair is optional but often welcome. Keep everything else at a quiet distance.
        </SectionText>
      </div>

      {/* Section 6 */}
      <div className="mb-14">
        <SectionHeading>Choose decor that supports quiet comfort</SectionHeading>
        <SectionText>
          Every object near a reading corner should earn its place. A small stack of current books with spines facing out, a ceramic vessel in a warm neutral glaze, a candle in a simple holder, a single stem or dried botanical in a narrow vase, and perhaps a quiet framed piece of art on the wall above — that is quite enough. The goal is not decoration but atmosphere. Each object should make the corner feel more personal and more settled, not more styled or more full.
        </SectionText>
      </div>
    </div>
  );
}
